import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { AutoFixHigh, Language, PlayArrow, WarningAmber } from "@mui/icons-material";
import Swal from "sweetalert2";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import LoadingBackdrop from "../../Common/LoadingBackdrop";
import { fetchAllSessions } from "../../../actions/sessionActions";
import { bulkRegenerateCaptions, bulkReapplyVocabulary, bulkTranslateCaptions } from "../../../actions/captionsActions";
import { STATUS_COLOR, prettySource } from "../../../constants/captionStyle";

// This page only ever generates English source transcripts via
// Whisper / AssemblyAI — there is no Spanish "generation", Spanish
// captions are created downstream via the per-session Translate
// button on an approved English track. Hardcoding the dispatch
// language here removes the misleading dropdown that used to imply
// otherwise (operators were picking Spanish + Generate and getting
// nothing useful back).
const GENERATION_LANGUAGE = "en";

// Bulk-translate target language picker options. Keys are the
// language codes APIv2's TranslateCaptionJob accepts (see
// TranslationPromptBuilder::humanLanguageName for the canonical
// list); labels are the operator-facing names. Adding a new
// language here is cheap, but the backend also needs an active
// glossary + instructions row for it to translate meaningfully —
// today only `es` has those, the others would translate against
// the default prompt.
const TRANSLATE_TARGETS = [
  { code: "es", label: "Spanish" },
  { code: "pt-br", label: "Portuguese (BR)" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
];

// Page-local METADATA for each CaptionStatus — the long-form label
// shown in the chip's tooltip and an optional `needsAttention` flag
// that lights up a warning icon on the chip. Colours are sourced
// from the shared STATUS_COLOR map in constants/captionStyle.js
// (also used by the Editor + Review Queue) so all caption surfaces
// stay in lockstep on chip colours. Keep keys in sync with
// App\Enum\CaptionStatus on APIv2 — a missing key falls through to
// a default grey chip with the raw enum string in the tooltip,
// which is loud enough to notice.
const STATUS_META = {
  human_reviewed: { label: "Approved" },
  human_edited: { label: "Approved (edited)" },
  // "(live)" suffix added in release-2026-05-19 when APIv2 relaxed
  // servability: auto_pending_review captions reach end users
  // immediately while still showing up in the operator's Review
  // Queue for QA. The amber colour still signals "needs attention",
  // but the label clarifies that the row is reaching users in the
  // meantime — operators were assuming "pending review" meant "not
  // live yet" under the previous gate.
  auto_pending_review: { label: "Pending review (live)" },
  processing: { label: "Processing" },
  // Dispatched to the queue but a worker hasn't picked it up yet.
  // Pre-created by APIv2's controller / observer at dispatch time so
  // the operator gets immediate "your click landed" feedback in this
  // list without waiting for the worker.
  queued: { label: "Queued" },
  // Failed tracks get a warning icon in addition to the red color —
  // "needs reprocessing" is the one state where re-firing generation
  // from this page is the right next step (vs. pending review which
  // wants the Review Queue, or processing which is mid-flight).
  failed: { label: "Failed — needs reprocessing", needsAttention: true },
};

const statusChipFor = status => ({
  color: STATUS_COLOR[status] || "default",
  label: STATUS_META[status]?.label || status,
  needsAttention: !!STATUS_META[status]?.needsAttention,
});

// Humanized labels for the per-row `reason` enum returned by APIv2's
// bulk caption endpoints (regenerate-bulk, translate-bulk,
// reapply-vocab-bulk, approve-bulk). Keep keys in sync with the API
// — see cms-api-openapi.yaml for the canonical list. A missing key
// falls through to the raw enum string, which is loud enough to
// notice if APIv2 adds a new reason without updating this map.
const BULK_SKIP_REASON_LABELS = {
  not_found: "session not found / deleted",
  no_audio: "missing audio URL",
  no_approved_source: "no approved English source",
  not_pending_review: "not in pending review",
  no_captions: "no caption rows to update",
  no_changes: "vocabulary didn't match anything",
  skip_captions_enabled: "marked 'skip caption generation'",
};

/**
 * Build a comma-joined humanized list of unique skip reasons from
 * the bulk endpoint's `details` array. Used in the result Swal so
 * the parenthetical text always reflects the actual reasons the
 * server returned, instead of being hardcoded to a fixed list that
 * goes stale every time a new reason ships.
 */
function humanizeBulkSkipReasons(details) {
  const reasons = (details ?? []).filter(d => d?.status === "skipped" && d?.reason).map(d => d.reason);
  const unique = [...new Set(reasons)];
  if (unique.length === 0) return "see browser console for per-session details";
  return unique.map(r => BULK_SKIP_REASON_LABELS[r] || r).join(", ");
}

/**
 * Operator-facing "kick off English caption generation for a batch
 * of sessions" view. Built on top of the /admin/sessions endpoint,
 * which returns a lean `captions: [{language_code, status, source}]`
 * summary per row.
 *
 * - The Tracks column renders one chip per existing track, color-coded
 *   by status, with a warning icon on failed tracks.
 * - The "Hide already-captioned" filter (default on) keeps the list
 *   focused on sessions still missing an English transcript.
 * - There is no language picker: Spanish captions are made via the
 *   per-session Translate button on an approved English track, not
 *   from this page.
 */
const CaptionsSessionsListPage = () => {
  const dispatch = useDispatch();
  // sessionsReducer stores the list under `all_sessions`, not `sessions` — the
  // reducer slice key (`sessions`) collides with its inner state name.
  const sessions = useSelector(state => state.sessions.all_sessions);
  const isLoading = useSelector(state => state.sessions.isLoading);

  const [search, setSearch] = useState("");
  // Default ON: operators are here to caption sessions that haven't
  // been done yet. Showing the already-captioned ones by default just
  // adds noise and risks an accidental re-dispatch.
  const [hideAlreadyCaptioned, setHideAlreadyCaptioned] = useState(true);
  // Default ON: inactive sessions (drafts, archived) usually aren't
  // worth caption budget — they're not live to users. Operator can
  // tick "Include inactive sessions" for the draft-captioning case.
  const [includeInactive, setIncludeInactive] = useState(false);
  // Default OFF (i.e. "skip-captions sessions are hidden"). These
  // are silence-by-design rows the operator has marked as no-captions
  // — they don't need any operator attention on this page. A
  // "Show silent / no-caption sessions" toggle surfaces them when
  // the operator wants to review or un-flag.
  const [showSkipCaptions, setShowSkipCaptions] = useState(false);
  const [translateTarget, setTranslateTarget] = useState(TRANSLATE_TARGETS[0].code);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [selected, setSelected] = useState(() => new Set());
  const [dispatching, setDispatching] = useState(false);

  useEffect(() => {
    dispatch(fetchAllSessions());
  }, [dispatch]);

  // Search-only matches, ignoring the hide-already-captioned filter.
  // Tracked separately from `filtered` so the empty-state copy can
  // tell the difference between "search yielded zero matches" and
  // "search matched some sessions but they were all hidden by the
  // captioning filter" — picked up by bugbot on PR #399.
  const searchMatches = useMemo(() => {
    if (!Array.isArray(sessions)) return [];
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(
      s =>
        String(s.session_name ?? "")
          .toLowerCase()
          .includes(q) || String(s.session_id).includes(q)
    );
  }, [sessions, search]);

  const filtered = useMemo(() => {
    // "Already captioned" = any English caption row exists for this
    // session, regardless of status. A failed or stuck row still
    // counts as "already touched" — the operator should handle it
    // from the Review Queue (or re-fire from here only if the
    // warning chip is the reason they came back), rather than blindly
    // re-dispatching from a list that doesn't surface the existing
    // row's state.
    const hasEnglishTrack = s =>
      Array.isArray(s.captions) && s.captions.some(c => c?.language_code === GENERATION_LANGUAGE);
    return searchMatches.filter(s => {
      if (hideAlreadyCaptioned && hasEnglishTrack(s)) return false;
      if (!includeInactive && !s.session_is_active) return false;
      // Silence-by-design sessions: hidden by default; the operator
      // can surface them via the "Show silent / no-caption sessions"
      // toggle to review or un-flag.
      if (!showSkipCaptions && s.session_skip_captions) return false;
      return true;
    });
  }, [searchMatches, hideAlreadyCaptioned, includeInactive, showSkipCaptions]);

  // Which filter is responsible when the search produced matches but
  // none of them survive into `filtered`. Multi-axis: any combination
  // of (hideAlreadyCaptioned, !includeInactive, !showSkipCaptions)
  // can be the killer. Per axis we count "matches that would surface
  // if ONLY this filter were toggled off (all other filters still
  // apply)" — that count is what the empty-state message reports, so
  // a partial-axis case (e.g. 1 of 10 matches is skip-captions, the
  // other 9 are captioned) doesn't get misattributed to a single
  // axis as if it covered every match.
  //
  // History: bugbot has caught this three times — once when
  // `includeInactive` was added without updating the analysis, again
  // when `showSkipCaptions` was added, and again when the message
  // claimed "all" while only *some* matches actually had that
  // property. Returning counts instead of booleans is the fix.
  const hideEmptyAnalysis = useMemo(() => {
    if (!search.trim() || searchMatches.length === 0 || filtered.length > 0) {
      return { captioned: 0, inactive: 0, skipCaptions: 0 };
    }
    const hasEnglishTrack = s =>
      Array.isArray(s.captions) && s.captions.some(c => c?.language_code === GENERATION_LANGUAGE);
    // For each filter axis, return search matches that would remain if
    // ONLY this filter were toggled off (all other filters still apply).
    // The length of that list is the axis's "actually-hidden by ME" count.
    const applyAllExcept = axis =>
      searchMatches.filter(s => {
        if (axis !== "captioned" && hideAlreadyCaptioned && hasEnglishTrack(s)) return false;
        if (axis !== "inactive" && !includeInactive && !s.session_is_active) return false;
        if (axis !== "skipCaptions" && !showSkipCaptions && s.session_skip_captions) return false;
        return true;
      });
    return {
      captioned: hideAlreadyCaptioned ? applyAllExcept("captioned").length : 0,
      inactive: !includeInactive ? applyAllExcept("inactive").length : 0,
      skipCaptions: !showSkipCaptions ? applyAllExcept("skipCaptions").length : 0,
    };
  }, [search, searchMatches, filtered, hideAlreadyCaptioned, includeInactive, showSkipCaptions]);
  // Precedence: skipCaptions wins (most specific — operator explicitly
  // opted these out), then captioned, then inactive. The ternary chain
  // in the empty-state JSX already short-circuits on the first non-zero
  // count, so no extra precedence flags are needed here.
  const hiddenBySkipCaptionsCount = hideEmptyAnalysis.skipCaptions;
  const hiddenByCaptionedCount = hideEmptyAnalysis.captioned;
  const hiddenByInactiveCount = hideEmptyAnalysis.inactive;

  // Drop selections that no longer appear in the filtered list so the
  // selection bar's count never disagrees with what's visible (and
  // toggling the hide filter or typing into search can't strand a
  // selection on a now-hidden row).
  useEffect(() => {
    if (selected.size === 0) return;
    const visibleIds = new Set(filtered.map(s => s.session_id));
    let mutated = false;
    const next = new Set(selected);
    for (const id of next) {
      if (!visibleIds.has(id)) {
        next.delete(id);
        mutated = true;
      }
    }
    if (mutated) setSelected(next);
    // `selected` intentionally omitted from deps — we only want to
    // re-prune when the *filtered* set changes, not on every
    // selection toggle (which would loop). The setSelected callback
    // form would also work but adds churn for the common no-op case.
  }, [filtered]);

  const paged = useMemo(() => filtered.slice(page * perPage, (page + 1) * perPage), [filtered, page, perPage]);

  const selectedRows = useMemo(() => filtered.filter(s => selected.has(s.session_id)), [filtered, selected]);

  const costEstimate = useMemo(() => {
    const totalSec = selectedRows.reduce((acc, s) => acc + (Number(s.session_length_in_sec) || 0), 0);
    return {
      count: selectedRows.length,
      minutes: totalSec / 60,
    };
  }, [selectedRows]);

  // Selected rows that have an APPROVED English caption — these are
  // the only ones eligible for the "Send for translation" path. The
  // server-side translate-bulk would skip the rest anyway with
  // reason="no_approved_source", but the count here lets us disable
  // the button cleanly + tell the operator "X of N selected can be
  // translated" instead of dispatching a request that no-ops.
  const translatableSelectedRows = useMemo(
    () =>
      selectedRows.filter(
        s =>
          Array.isArray(s.captions) &&
          s.captions.some(
            c =>
              c?.language_code === GENERATION_LANGUAGE && (c.status === "human_reviewed" || c.status === "human_edited")
          )
      ),
    [selectedRows]
  );

  const toggleAllOnPage = checked => {
    setSelected(prev => {
      const next = new Set(prev);
      paged.forEach(s => {
        if (checked) next.add(s.session_id);
        else next.delete(s.session_id);
      });
      return next;
    });
  };

  const toggleRow = sessionId => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  // After a successful bulk dispatch (regenerate OR translate) we
  // refetch the sessions list so the just-queued rows immediately
  // show their grey "Queued" chips, and reset to page 0 — see the
  // hide-already-captioned filter logic in `filtered` for why.
  const refreshAfterBulkDispatch = () => {
    dispatch(fetchAllSessions());
    setPage(0);
  };

  const onBulkDispatch = async () => {
    if (selectedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: `Generate captions for ${selectedRows.length} sessions?`,
      html: `~${costEstimate.minutes.toFixed(1)} min of audio will be sent to AssemblyAI.<br/>
             Each session will appear in the Review Queue once transcription finishes.`,
      showCancelButton: true,
      confirmButtonText: "Generate",
    });
    if (!isConfirmed) return;

    setDispatching(true);
    try {
      // One HTTP request, N queue jobs server-side. Old loop fired
      // one request per session and was tripping throttle:api on
      // 1000+-session backfills.
      const ids = selectedRows.map(s => s.session_id);
      const result = await dispatch(
        bulkRegenerateCaptions(ids, { language: GENERATION_LANGUAGE, provider: "assemblyai" })
      );
      setSelected(new Set());
      if ((result?.queued ?? 0) > 0) refreshAfterBulkDispatch();
      Swal.fire({
        icon: (result?.skipped ?? 0) === 0 ? "success" : "warning",
        title: `Queued ${result?.queued ?? 0} session${(result?.queued ?? 0) === 1 ? "" : "s"}`,
        text:
          (result?.skipped ?? 0) === 0
            ? "Each session now shows a grey 'Queued' chip in Tracks, escalating to blue 'Processing' as workers pick them up, then amber 'Pending review' when transcription finishes."
            : `${result.skipped} skipped (${humanizeBulkSkipReasons(result?.details)}). Each queued session shows a grey 'Queued' chip in Tracks immediately.`,
      });
    } finally {
      setDispatching(false);
    }
  };

  const onBulkTranslate = async () => {
    const targetMeta = TRANSLATE_TARGETS.find(t => t.code === translateTarget);
    if (translatableSelectedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: `Translate ${translatableSelectedRows.length} session${translatableSelectedRows.length === 1 ? "" : "s"} to ${targetMeta?.label ?? translateTarget}?`,
      html:
        translatableSelectedRows.length === selectedRows.length
          ? `Every selected session has an approved English track. The translation will appear in the Review Queue when each job completes.`
          : `Only ${translatableSelectedRows.length} of ${selectedRows.length} selected sessions have an approved English track. The rest need EN approval first and will be skipped — translate them later.`,
      showCancelButton: true,
      confirmButtonText: "Translate",
    });
    if (!isConfirmed) return;

    setDispatching(true);
    try {
      const ids = translatableSelectedRows.map(s => s.session_id);
      const result = await dispatch(bulkTranslateCaptions(ids, translateTarget));
      setSelected(new Set());
      if ((result?.queued ?? 0) > 0) refreshAfterBulkDispatch();
      Swal.fire({
        icon: (result?.skipped ?? 0) === 0 ? "success" : "warning",
        title: `Queued ${result?.queued ?? 0} translation${(result?.queued ?? 0) === 1 ? "" : "s"}`,
        text:
          (result?.skipped ?? 0) === 0
            ? `Each ${targetMeta?.label ?? translateTarget} track will appear in the Review Queue as auto_pending_review when the job completes.`
            : `${result.skipped} skipped (${humanizeBulkSkipReasons(result?.details)}). Each queued translation will appear in the Review Queue when the job completes.`,
      });
    } finally {
      setDispatching(false);
    }
  };

  // Re-apply vocab/brand/alias transforms to existing VTTs without
  // re-burning AssemblyAI minutes. Works on EVERY caption row of the
  // selected sessions; sessions whose captions don't change (vocab
  // didn't match anything) come back as skipped/no_changes.
  const onBulkReapplyVocab = async () => {
    if (selectedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title: `Re-apply vocabulary to ${selectedRows.length} session${selectedRows.length === 1 ? "" : "s"}?`,
      text:
        "Re-runs author aliases + brand-term capitalization on existing VTTs. " +
        "No new audio transcription. Approval status is preserved.",
      showCancelButton: true,
      confirmButtonText: "Re-apply",
    });
    if (!isConfirmed) return;

    setDispatching(true);
    try {
      const ids = selectedRows.map(s => s.session_id);
      const result = await dispatch(bulkReapplyVocabulary(ids));
      setSelected(new Set());
      if ((result?.updated ?? 0) > 0) refreshAfterBulkDispatch();
      Swal.fire({
        icon: (result?.updated ?? 0) > 0 ? "success" : "info",
        title: `Updated ${result?.updated ?? 0} session${(result?.updated ?? 0) === 1 ? "" : "s"}`,
        text:
          (result?.updated ?? 0) === 0
            ? `Nothing changed — none of the selected sessions had captions whose text the current vocabulary would alter.`
            : (result?.skipped ?? 0) === 0
              ? "Captions updated in place. Approval status preserved."
              : `${result.skipped} session${result.skipped === 1 ? "" : "s"} skipped (${humanizeBulkSkipReasons(result?.details)}).`,
      });
    } finally {
      setDispatching(false);
    }
  };

  if (isLoading && !sessions) {
    return <LoadingBackdrop open />;
  }

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Generate English Captions
          </Typography>
        </Toolbar>
        {/* Selection bar — kept inside the colored AppBar so it stays
            sticky with the title, and so the warning palette pops. */}
        {/* Use selectedRows.length (the intersection with the search-filtered
            list), not selected.size — otherwise typing a search that
            hides all selected rows would leave the bar visible with a
            count of 0 and a Generate button that silently no-ops. */}
        {selectedRows.length > 0 && (
          <Toolbar sx={{ backgroundColor: "primary.dark", minHeight: 56, gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ flexGrow: 1, color: "inherit" }} variant="body2">
              Selected: <strong>{costEstimate.count}</strong> session{costEstimate.count === 1 ? "" : "s"}
              {" · "}~{costEstimate.minutes.toFixed(1)} min
              {translatableSelectedRows.length > 0 && translatableSelectedRows.length < selectedRows.length && (
                <>
                  {" · "}
                  <strong>{translatableSelectedRows.length}</strong> with approved EN
                </>
              )}
            </Typography>
            <FormControl
              size="small"
              sx={{ minWidth: 150, backgroundColor: "rgba(255,255,255,0.12)", borderRadius: 1 }}
            >
              <InputLabel id="translate-target-label" sx={{ color: "common.white" }}>
                Translate to
              </InputLabel>
              <Select
                labelId="translate-target-label"
                value={translateTarget}
                label="Translate to"
                onChange={e => setTranslateTarget(e.target.value)}
                sx={{ color: "common.white" }}
              >
                {TRANSLATE_TARGETS.map(t => (
                  <MenuItem key={t.code} value={t.code}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip
              title={
                translatableSelectedRows.length === 0
                  ? "None of the selected sessions have an approved English track to translate from."
                  : `Send ${translatableSelectedRows.length} session${translatableSelectedRows.length === 1 ? "" : "s"} for translation.`
              }
            >
              <span>
                <Button
                  startIcon={<Language />}
                  variant="outlined"
                  color="inherit"
                  disabled={dispatching || translatableSelectedRows.length === 0}
                  onClick={onBulkTranslate}
                  sx={{ color: "common.white", borderColor: "rgba(255,255,255,0.5)" }}
                >
                  Send for translation
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Re-run author aliases + brand-term capitalization on existing VTTs. No audio re-transcription. Approval status preserved per row.">
              <span>
                <Button
                  startIcon={<AutoFixHigh />}
                  variant="outlined"
                  color="inherit"
                  disabled={dispatching}
                  onClick={onBulkReapplyVocab}
                  sx={{ color: "common.white", borderColor: "rgba(255,255,255,0.5)" }}
                >
                  Re-apply vocabulary
                </Button>
              </span>
            </Tooltip>
            <Button
              startIcon={<PlayArrow />}
              variant="contained"
              color="warning"
              disabled={dispatching}
              onClick={onBulkDispatch}
            >
              Generate captions for selected
            </Button>
          </Toolbar>
        )}
      </AppBar>

      {/* Filter bar — rendered in default (light) palette so the
          TextField doesn't inherit the AppBar's white-on-primary color
          context. */}
      <Toolbar
        sx={{
          backgroundColor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 2,
          py: 1.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Search sessions"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 280, flexGrow: 1, maxWidth: 480 }}
        />
        <Tooltip title="A session counts as 'already captioned' if any English caption row exists, regardless of status. Failed rows show the warning chip in Tracks — re-fire generation from here if reprocessing is the right action; otherwise handle them from the Review Queue.">
          <FormControlLabel
            control={
              <Checkbox
                checked={hideAlreadyCaptioned}
                onChange={e => {
                  setHideAlreadyCaptioned(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Hide sessions already captioned in English"
          />
        </Tooltip>
        <Tooltip title="Inactive sessions (drafts, archived) are hidden by default — they're not live to users so they usually aren't worth caption budget. Tick this to surface them for draft-captioning ahead of launch.">
          <FormControlLabel
            control={
              <Checkbox
                checked={includeInactive}
                onChange={e => {
                  setIncludeInactive(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Include inactive sessions"
          />
        </Tooltip>
        <Tooltip title="Sessions marked 'skip caption generation' (silence-by-design, background-music-only) are hidden by default — they don't need operator attention here. Tick to review or un-flag them.">
          <FormControlLabel
            control={
              <Checkbox
                checked={showSkipCaptions}
                onChange={e => {
                  setShowSkipCaptions(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Show silent / no-caption sessions"
          />
        </Tooltip>
        {/* "Select all filtered" — selects every row in `filtered`
            (cross-page), not just the current TablePagination slice
            like the header checkbox does. Lets an operator queue an
            entire backlog in one click without paginating through the
            list selecting page-by-page. Disabled when there's nothing
            to add (zero filtered, or every filtered row is already
            selected). */}
        <Tooltip
          title={
            filtered.length === 0
              ? "Nothing to select — adjust the filter or search to surface sessions."
              : "Select every session the current filter shows, across all pages. Then click Generate to queue them all."
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              disabled={filtered.length === 0 || selectedRows.length === filtered.length}
              onClick={() => setSelected(new Set(filtered.map(s => s.session_id)))}
            >
              Select all {filtered.length} filtered
            </Button>
          </span>
        </Tooltip>
      </Toolbar>

      <Box sx={{ py: 2 }}>
        <TableContainer component={Paper} sx={{ marginTop: 2, boxShadow: 2, borderRadius: 1, overflow: "hidden" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      paged.some(s => selected.has(s.session_id)) && !paged.every(s => selected.has(s.session_id))
                    }
                    checked={paged.length > 0 && paged.every(s => selected.has(s.session_id))}
                    onChange={e => toggleAllOnPage(e.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <strong>ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Session</strong>
                </TableCell>
                <TableCell>
                  <strong>Duration</strong>
                </TableCell>
                <TableCell>
                  <strong>Active</strong>
                </TableCell>
                <TableCell>
                  <strong>Tracks</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      {
                        // Branch order matters. Each line excludes the
                        // state the next one would otherwise mis-describe:
                        //   1. zero sessions in the system (API returned [],
                        //      or initial load before fetch resolves) — fix
                        //      for the case where hideAlreadyCaptioned was
                        //      defaulting us into "untick to see the rest"
                        //      when there was nothing to unhide
                        //   2. user typed a search that matched nothing
                        //   3. search matched some, but hide filter ate
                        //      all of them — surface the count + action
                        //   4. no search; sessions exist but hide ate all
                        //   5. fallback (shouldn't normally fire here since
                        //      paged.length === 0 implies one of the above)
                        // `search.trim()` consistently — raw `search` is
                        // truthy for whitespace-only input.
                        searchMatches.length === 0 && !search.trim()
                          ? "No sessions available."
                          : searchMatches.length === 0 && search.trim()
                            ? "No sessions match the search."
                            : hiddenBySkipCaptionsCount > 0
                              ? `${hiddenBySkipCaptionsCount} of ${searchMatches.length} matching session${searchMatches.length === 1 ? "" : "s"} ${hiddenBySkipCaptionsCount === 1 ? "is" : "are"} marked 'skip caption generation'. Tick "Show silent / no-caption sessions" to see ${hiddenBySkipCaptionsCount === 1 ? "it" : "them"}.`
                              : hiddenByCaptionedCount > 0
                                ? `${hiddenByCaptionedCount} of ${searchMatches.length} matching session${searchMatches.length === 1 ? "" : "s"} ${hiddenByCaptionedCount === 1 ? "is" : "are"} already captioned in English. Untick "Hide sessions already captioned in English" to see ${hiddenByCaptionedCount === 1 ? "it" : "them"}.`
                                : hiddenByInactiveCount > 0
                                  ? `${hiddenByInactiveCount} of ${searchMatches.length} matching session${searchMatches.length === 1 ? "" : "s"} ${hiddenByInactiveCount === 1 ? "is" : "are"} inactive. Tick "Include inactive sessions" to see ${hiddenByInactiveCount === 1 ? "it" : "them"}.`
                                  : hideAlreadyCaptioned
                                    ? 'No sessions left to caption in English — untick "Hide sessions already captioned in English" to see the rest.'
                                    : "No sessions available."
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paged.map(s => (
                  <TableRow
                    key={s.session_id}
                    hover
                    role="checkbox"
                    selected={selected.has(s.session_id)}
                    onClick={() => toggleRow(s.session_id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(s.session_id)} />
                    </TableCell>
                    <TableCell>{s.session_id}</TableCell>
                    <TableCell>{s.session_name}</TableCell>
                    <TableCell>
                      {s.session_length_in_sec ? `${Math.round(s.session_length_in_sec / 60)} min` : "—"}
                    </TableCell>
                    <TableCell>
                      {s.session_is_active ? (
                        <Chip label="Active" color="success" size="small" />
                      ) : (
                        <Chip label="Inactive" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {s.session_skip_captions ? (
                        // Session is flagged as no-captions (silence-by-
                        // design); skip rendering chips entirely. The
                        // operator only sees these rows when they tick
                        // "Show silent / no-caption sessions", and the
                        // chip below makes the state unambiguous.
                        <Tooltip title="This session is marked 'skip caption generation'. Uncheck the flag on the session editor to enable captioning.">
                          <Chip label="No captions (silent)" size="small" variant="outlined" />
                        </Tooltip>
                      ) : Array.isArray(s.captions) && s.captions.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {s.captions.map(c => {
                            const meta = statusChipFor(c.status);
                            return (
                              <Tooltip
                                key={`${c.language_code}:${c.source}`}
                                title={`${meta.label} (${prettySource(c.source)})`}
                              >
                                <Chip
                                  // `icon` only renders for the warning case — keeps
                                  // healthy chips visually compact and reserves the
                                  // ⚠ glyph as a real "look at me" signal.
                                  icon={meta.needsAttention ? <WarningAmber fontSize="small" /> : undefined}
                                  label={String(c.language_code || "").toUpperCase()}
                                  color={meta.color}
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={e => {
              setPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />
        </TableContainer>
      </Box>
    </MainContentContainer>
  );
};

export default CaptionsSessionsListPage;
